import React, { useMemo, useState } from "react";
import { DatePeriodPicker } from "./DatePeriodPicker";

type LateOrder = {
  orderId: string;
  customer: string;
  brand: string;
  location: string;
  age: string;
  verdict?: string;
  rootCause?: string;
};

type FastOrder = {
  orderId: string;
  customer: string;
  brand: string;
  location: string;
  age: number;
};

type FastestByLocation = {
  location: string;
  orders: FastOrder[];
};

type Day = {
  date: string;
  totals: any;
  brands: any[];
  locations: any[];
  lateOrders: LateOrder[];
  fastestByLocation?: FastestByLocation[];
};

type BranchMTD = {
  location: string;
  completed: number;
  over60: number;
  pct: number;
  prevCompleted: number;
  prevOver60: number;
  prevPct: number;
  deltaPp: number;
  trend: "up" | "down" | "flat";
};

type BranchMonthly = {
  month: string;
  asOf: string;
  branches: BranchMTD[];
};

type Props = {
  days: Day[];
  branchMonthly?: BranchMonthly;
};

type TaggedOrder = LateOrder & { date: string };

// ── Month-to-date strip: % of orders over 1 hour per branch ──────────
function MonthlyLateStrip({ data }: { data?: BranchMonthly }) {
  if (!data || !data.branches || data.branches.length === 0) return null;

  return (
    <div style={{ marginBottom: 24 }}>
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: "var(--ct-ink-2)",
          marginBottom: 10,
        }}
      >
        Orders over 1 hour — month to date{" "}
        <span style={{ color: "var(--ct-ink-3)", fontWeight: 400 }}>
          · {data.month}
        </span>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
        {data.branches.map((b) => {
          const noBaseline = b.prevCompleted === 0;
          const good = b.trend === "down";
          const bad = b.trend === "up";
          const arrow = good ? "▼" : bad ? "▲" : "—";
          const trendColor = noBaseline
            ? "var(--ct-ink-3)"
            : good
            ? "var(--ct-ok)"
            : bad
            ? "var(--ct-crit)"
            : "var(--ct-ink-3)";

          return (
            <div
              key={b.location}
              style={{
                flex: "1 1 180px",
                minWidth: 180,
                borderRadius: 10,
                border: "1px solid var(--ct-line)",
                background: "var(--ct-surface)",
                padding: "14px 16px",
              }}
            >
              <div
                style={{
                  fontSize: 12.5,
                  fontWeight: 500,
                  color: "var(--ct-ink-2)",
                  marginBottom: 8,
                }}
              >
                {b.location}
              </div>

              <div
                className="ct-num"
                style={{
                  fontSize: 26,
                  fontWeight: 650,
                  letterSpacing: "-0.02em",
                  lineHeight: 1.1,
                  color: "var(--ct-ink)",
                }}
              >
                {b.pct}%
              </div>

              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: trendColor,
                  marginTop: 6,
                  whiteSpace: "nowrap",
                }}
              >
                {noBaseline ? (
                  <span style={{ fontWeight: 400 }}>no last-month data</span>
                ) : (
                  <>
                    {arrow} {Math.abs(b.deltaPp)} pp{" "}
                    <span style={{ color: "var(--ct-ink-3)", fontWeight: 400 }}>
                      vs last month
                    </span>
                  </>
                )}
              </div>

              <div
                style={{
                  fontSize: 11.5,
                  color: "var(--ct-ink-3)",
                  marginTop: 4,
                }}
              >
                {b.over60} of {b.completed} orders
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BlameCell({ verdict }: { verdict?: string }) {
  const v = (verdict || "").trim();
  if (!v) {
    return <span style={{ color: "var(--ct-ink-3)" }}>—</span>;
  }

  const isBoth = v.toLowerCase().indexOf("both") !== -1;

  if (isBoth) {
    return (
      <span
        style={{
          display: "inline-block",
          padding: "4px 10px",
          borderRadius: 20,
          fontSize: 12,
          fontWeight: 600,
          color: "var(--ct-crit)",
          background: "var(--ct-crit-bg)",
          whiteSpace: "nowrap",
        }}
      >
        {v}
      </span>
    );
  }

  return (
    <span
      style={{
        fontSize: 13.5,
        fontWeight: 500,
        color: "var(--ct-ink)",
        whiteSpace: "nowrap",
      }}
    >
      {v}
    </span>
  );
}

type TaggedFastOrder = FastOrder & { date: string };

function FastestOrdersTable({ days }: { days: Day[] }) {
  const rows: TaggedFastOrder[] = useMemo(() => {
    const byLoc: Record<string, TaggedFastOrder[]> = {};
    for (const d of days) {
      for (const entry of d.fastestByLocation || []) {
        if (!byLoc[entry.location]) byLoc[entry.location] = [];
        for (const o of entry.orders) {
          byLoc[entry.location].push({ ...o, date: d.date });
        }
      }
    }
    const all: TaggedFastOrder[] = [];
    for (const [, orders] of Object.entries(byLoc)) {
      orders.sort((a, b) => a.age - b.age);
      all.push(...orders.slice(0, 3));
    }
    all.sort((a, b) => a.location.localeCompare(b.location) || a.age - b.age);

    return all;
  }, [days]);

  if (rows.length === 0) return null;

  const HEADERS = ["Date", "Order", "Age", "Customer", "Brand", "Location"];

  return (
    <div
      style={{
        borderRadius: 10,
        border: "1px solid var(--ct-line)",
        background: "var(--ct-surface)",
        overflow: "hidden",
        width: "100%",
        marginTop: 16,
      }}
    >
      <div
        style={{
          padding: "13px 16px",
          fontSize: 14,
          fontWeight: 600,
          letterSpacing: "-0.01em",
          color: "var(--ct-ink)",
          borderBottom: "1px solid var(--ct-line)",
        }}
      >
        Fastest 3 orders by location
      </div>

      <div style={{ overflowX: "auto", width: "100%" }}>
        <table
          style={{
            width: "100%",
            minWidth: 700,
            borderCollapse: "collapse",
            fontSize: 13.5,
          }}
        >
          <thead>
            <tr style={{ background: "var(--ct-surface-2)" }}>
              {HEADERS.map((h) => (
                <th
                  key={h}
                  style={{
                    padding: "10px 16px",
                    textAlign: "left",
                    fontSize: 12,
                    fontWeight: 500,
                    color: "var(--ct-ink-3)",
                    borderBottom: "1px solid var(--ct-line)",
                    whiteSpace: "nowrap",
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {rows.map((r, idx) => (
              <tr
                key={r.date + r.orderId + idx}
                style={{
                  borderBottom:
                    idx === rows.length - 1
                      ? "none"
                      : "1px solid var(--ct-line-soft)",
                  transition: "background .12s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "var(--ct-row-hover)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                }}
              >
                <td
                  className="ct-num"
                  style={{
                    padding: "14px 16px",
                    whiteSpace: "nowrap",
                    color: "var(--ct-ink-3)",
                  }}
                >
                  {r.date}
                </td>

                <td style={{ padding: "14px 16px", whiteSpace: "nowrap" }}>
                  <span
                    className="ct-num"
                    style={{
                      fontWeight: 600,
                      color: "var(--ct-accent)",
                    }}
                  >
                    {r.orderId.length > 8
                      ? r.orderId.slice(0, 8) + "…"
                      : r.orderId}
                  </span>
                </td>

                <td
                  className="ct-num"
                  style={{
                    padding: "14px 16px",
                    whiteSpace: "nowrap",
                    fontSize: 15,
                    fontWeight: 600,
                    color: "var(--ct-ok)",
                  }}
                >
                  {r.age}m
                </td>

                <td
                  style={{
                    padding: "14px 16px",
                    whiteSpace: "nowrap",
                    fontWeight: 600,
                    color: "var(--ct-ink)",
                  }}
                >
                  {r.customer}
                </td>

                <td
                  style={{
                    padding: "14px 16px",
                    whiteSpace: "nowrap",
                    color: "var(--ct-ink)",
                  }}
                >
                  {r.brand}
                </td>

                <td
                  style={{
                    padding: "14px 16px",
                    whiteSpace: "nowrap",
                    color: "var(--ct-ink-2)",
                  }}
                >
                  {r.location}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function LateOrdersLog({ days, branchMonthly }: Props) {
  // null range = single-day stepping at dayIndex; a range = custom period from the picker
  const [range, setRange] = useState<{ from: string; to: string } | null>(null);
  const [dayIndex, setDayIndex] = useState(0); // 0 = latest day

  // ── CSV export (operators) ─────────────────────────────────────────
  const nowMonthSAST = () => {
    const n = new Date(new Date().toLocaleString("en-US", { timeZone: "Africa/Johannesburg" }));
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}`;
  };
  const [exportMonth, setExportMonth] = useState<string>(nowMonthSAST());
  const [exporting, setExporting] = useState(false);
  const exportLateOrders = async () => {
    setExporting(true);
    try {
      const res = await fetch(`/.netlify/functions/export-late-orders?month=${encodeURIComponent(exportMonth)}`);
      if (!res.ok) throw new Error(await res.text());
      const text = await res.text();
      const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `late-orders-${exportMonth}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      console.error("EXPORT_LATE:", e);
    } finally {
      setExporting(false);
    }
  };

  const newestDate = days[0]?.date || "";
  const oldestDate = days[days.length - 1]?.date || "";
  const lastIndex = Math.max(0, days.length - 1);
  const safeIndex = Math.min(dayIndex, lastIndex);

  const usingRange = range !== null;

  const shownDays = usingRange
    ? days.filter((d) => d.date >= range!.from && d.date <= range!.to)
    : days[safeIndex]
    ? [days[safeIndex]]
    : [];

  const stepOlder = () => {
    setRange(null);
    setDayIndex((i) => Math.min(i + 1, lastIndex));
  };
  const stepNewer = () => {
    setRange(null);
    setDayIndex((i) => Math.max(i - 1, 0));
  };
  const goLatest = () => {
    setRange(null);
    setDayIndex(0);
  };

  const atNewest = !usingRange && safeIndex === 0;
  const atOldest = !usingRange && safeIndex >= lastIndex;

  const dayLabel = usingRange
    ? range!.from === range!.to
      ? range!.from
      : `${range!.from} → ${range!.to}`
    : days[safeIndex]?.date || "Latest";

  const rows: TaggedOrder[] = [];
  for (const d of shownDays) {
    for (const o of d.lateOrders || []) {
      rows.push({ ...o, date: d.date });
    }
  }

  const navBtn = (disabled: boolean): React.CSSProperties => ({
    width: 32,
    height: 32,
    borderRadius: 8,
    border: "1px solid var(--ct-line)",
    background: "var(--ct-surface)",
    color: disabled ? "var(--ct-ink-3)" : "var(--ct-ink-2)",
    cursor: disabled ? "not-allowed" : "pointer",
    fontSize: 16,
    display: "grid",
    placeItems: "center",
    fontFamily: "inherit",
  });

  const HEADERS = [
    "Date",
    "Order",
    "Age",
    "Customer",
    "Brand",
    "Location",
    "Blame",
    "Root cause",
  ];

  return (
    <main style={{ padding: "20px 24px 40px" }}>
      {/* Month-to-date per-branch strip */}
      <MonthlyLateStrip data={branchMonthly} />

      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
          marginBottom: 20,
        }}
      >
        <div style={{ fontSize: 13, color: "var(--ct-ink-3)" }}>
          {usingRange ? "Selected period" : atNewest ? "Latest capture" : dayLabel} ·{" "}
          {rows.length} order{rows.length === 1 ? "" : "s"}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          {/* Day stepper: ‹ older · date · newer › */}
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <button
              onClick={stepOlder}
              disabled={atOldest}
              title="Older day"
              style={navBtn(atOldest)}
            >
              ‹
            </button>

            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: "var(--ct-ink)",
                minWidth: 150,
                textAlign: "center",
                whiteSpace: "nowrap",
              }}
            >
              {atNewest ? `${dayLabel} · latest` : dayLabel}
            </div>

            <button
              onClick={stepNewer}
              disabled={atNewest}
              title="Newer day"
              style={navBtn(atNewest)}
            >
              ›
            </button>

            {!atNewest && (
              <button
                onClick={goLatest}
                style={{
                  padding: "6px 12px",
                  borderRadius: 8,
                  border: "1px solid var(--ct-line)",
                  background: "var(--ct-surface)",
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: 500,
                  color: "var(--ct-ink-2)",
                  fontFamily: "inherit",
                }}
              >
                Latest
              </button>
            )}
          </div>

          <DatePeriodPicker
            buttonLabel={dayLabel}
            defaultFrom={oldestDate}
            defaultTo={newestDate}
            onApply={(from, to) => {
              setDayIndex(0);
              setRange({ from, to });
            }}
          />

          {/* Month CSV export */}
          <input
            type="month"
            value={exportMonth}
            onChange={(e) => setExportMonth(e.target.value)}
            style={{
              border: "1px solid var(--ct-line)",
              background: "var(--ct-surface)",
              color: "var(--ct-ink)",
              borderRadius: 8,
              padding: "7px 10px",
              fontFamily: "inherit",
              fontSize: 13,
            }}
          />
          <button
            onClick={exportLateOrders}
            disabled={exporting}
            style={{
              border: "1px solid var(--ct-line)",
              background: "var(--ct-surface)",
              color: "var(--ct-ink)",
              borderRadius: 8,
              padding: "7px 13px",
              fontFamily: "inherit",
              fontSize: 13,
              fontWeight: 600,
              cursor: exporting ? "default" : "pointer",
            }}
          >
            {exporting ? "Exporting…" : "Export month (CSV)"}
          </button>
        </div>
      </div>

      {/* ORDERS OVER 1 HOUR */}
      <div
        style={{
          borderRadius: 10,
          border: "1px solid var(--ct-line)",
          background: "var(--ct-surface)",
          overflow: "hidden",
          width: "100%",
        }}
      >
        <div
          style={{
            padding: "13px 16px",
            fontSize: 14,
            fontWeight: 600,
            letterSpacing: "-0.01em",
            color: "var(--ct-ink)",
            borderBottom: "1px solid var(--ct-line)",
          }}
        >
          Orders over 1 hour
        </div>

        <div style={{ overflowX: "auto", width: "100%" }}>
          <table
            style={{
              width: "100%",
              minWidth: 1040,
              borderCollapse: "collapse",
              fontSize: 13.5,
            }}
          >
            <thead>
              <tr style={{ background: "var(--ct-surface-2)" }}>
                {HEADERS.map((h) => (
                  <th
                    key={h}
                    style={{
                      padding: "10px 16px",
                      textAlign: "left",
                      fontSize: 12,
                      fontWeight: 500,
                      color: "var(--ct-ink-3)",
                      borderBottom: "1px solid var(--ct-line)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {rows.map((r, idx) => (
                <tr
                  key={r.date + r.orderId + idx}
                  style={{
                    borderBottom:
                      idx === rows.length - 1
                        ? "none"
                        : "1px solid var(--ct-line-soft)",
                    transition: "background .12s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "var(--ct-row-hover)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  <td
                    className="ct-num"
                    style={{
                      padding: "14px 16px",
                      whiteSpace: "nowrap",
                      color: "var(--ct-ink-3)",
                    }}
                  >
                    {r.date}
                  </td>

                  <td style={{ padding: "14px 16px", whiteSpace: "nowrap" }}>
                    <span
                      title={r.orderId}
                      className="ct-num"
                      style={{
                        fontWeight: 600,
                        color: "var(--ct-accent)",
                      }}
                    >
                      {r.orderId.length > 8
                        ? r.orderId.slice(0, 8) + "…"
                        : r.orderId}
                    </span>
                  </td>

                  <td
                    className="ct-num"
                    style={{
                      padding: "14px 16px",
                      whiteSpace: "nowrap",
                      fontSize: 15,
                      fontWeight: 600,
                      color: "var(--ct-ink)",
                    }}
                  >
                    {r.age}
                  </td>

                  <td
                    style={{
                      padding: "14px 16px",
                      whiteSpace: "nowrap",
                      fontWeight: 600,
                      color: "var(--ct-ink)",
                    }}
                  >
                    {r.customer}
                  </td>

                  <td
                    style={{
                      padding: "14px 16px",
                      whiteSpace: "nowrap",
                      color: "var(--ct-ink)",
                    }}
                  >
                    {r.brand}
                  </td>

                  <td
                    style={{
                      padding: "14px 16px",
                      whiteSpace: "nowrap",
                      color: "var(--ct-ink-2)",
                    }}
                  >
                    {r.location}
                  </td>

                  <td style={{ padding: "14px 16px", whiteSpace: "nowrap" }}>
                    <BlameCell verdict={r.verdict} />
                  </td>

                  <td
                    title={r.rootCause}
                    style={{
                      padding: "14px 16px",
                      color: "var(--ct-ink-2)",
                      maxWidth: 380,
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      lineHeight: 1.45,
                    }}
                  >
                    {r.rootCause || "—"}
                  </td>
                </tr>
              ))}

              {rows.length === 0 && (
                <tr>
                  <td
                    colSpan={HEADERS.length}
                    style={{
                      textAlign: "center",
                      padding: "56px 16px",
                      fontSize: 13.5,
                      color: "var(--ct-ink-3)",
                    }}
                  >
                    No orders ran over an hour in this period.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <FastestOrdersTable days={shownDays} />
    </main>
  );
}
